import {Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {RouteService} from '../../../../services/route/route.service';
import {AdminService} from '../../../../services/admin/admin.service';
import {SnackService} from '../../../../services/snack/snack.service';
import {LanguageService} from '../../../../services/language/language.service';
import {Translation, TranslationPost, TranslationPut} from '../../../../generated/translation-client';
import {cloneDeep, isEqual} from 'lodash-es';
import {AuthService} from '../../../../services/auth/auth.service';
import {Subscription} from 'rxjs';
import {PendingChangesDialogComponent} from '../../../../components/pending-changes-dialog/pending-changes-dialog.component';
import {CONTINUE_WITHOUT_SAVING} from '../../../sales-terminal/sales-terminal.constants';

export interface TranslationsDialogData {
  key?: string;
}

@Component({
  selector: 'app-translations-view',
  templateUrl: './translations-view.component.html'
})
export class TranslationsViewComponent implements OnInit, OnDestroy {
  @Input() key: string;
  @Output() confirmChange: EventEmitter<any> = new EventEmitter<any>();

  public translation: Translation = null;
  public translationOrig: Translation = null;
  public sortedLanguages: string[] = [];
  private _subs: Subscription[] = [];

  constructor(public dialogRef: MatDialogRef<TranslationsViewComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
              private dialog: MatDialog,
              public routeService: RouteService,
              public languageService: LanguageService,
              private snackService: SnackService,
              private adminService: AdminService,
              private translateService: TranslateService,
              private authService: AuthService) {}

  public ngOnInit(): void {
    this.translation = {translations: {}} as Translation;
    setTimeout(() => this.syncModels(), 0);
    this.languageService.getLanguages().subscribe((languages) =>
      this.sortedLanguages = this.languageService.sortLanguages(languages, this.authService.salesPointLanguage));
    if (this.isEdit) {
      this.getTranslation(this.routeService.params['key']);
    }
    this.dialogRef.disableClose = true;
    this._subs.push(this.dialogRef.backdropClick().subscribe(() => {
      this.confirmClose();
    }));
  }
  public ngOnDestroy(): void {
    this.routeService.replaceUrl('/app/admin/translations');
    this._subs.forEach(s => s.unsubscribe());
    this._subs = [];
  }

  syncModels(): void {
    this.translationOrig = cloneDeep(this.translation);
  }

  get isModelChanged(): boolean {
    return !isEqual(this.translation, this.translationOrig);
  }

  public get isCreate(): boolean {
    return this.routeService.checkRoute(['admin', 'translations', 'create']);
  }
  public get isEdit(): boolean {
    return this.routeService.checkRoute(['admin', 'translations', 'edit']);
  }

  public get isValid(): boolean {
    if (!this.translation?.translations) {return false;}
    const key = this.translation.key?.trim();
    return key?.length && !key.includes(' ') && !!this.translation?.translations['en'];
  }

  public getTranslation(key: string): void {
    this.adminService.getTranslation(key, false).subscribe((translation) => {
      this.translation = translation;
      this.syncModels();
    });
  }

  public create(): void {
    this.adminService.createTranslation(this.getPostData()).subscribe((translation) => {
      this.translation = translation;
      this.snackService.showSuccess(this.translateService.instant('general.api_translations_successfully_created'));
      this.finishChange();
    });
  }

  public update(): void {
    this.adminService.updateTranslation(this.translation.key, this.getPutData()).subscribe((translation) => {
      this.translation = translation;
      this.snackService.showSuccess(this.translateService.instant('general.api_translations_successfully_updated'));
      this.finishChange();
    });
  }

  private getPostData(): TranslationPost {
    const data = cloneDeep(this.translation) as TranslationPost;
    data.isCentreBased = false;
    return data;
  }
  private getPutData(): TranslationPut {
    return cloneDeep(this.translation) as TranslationPut;
  }

  private finishChange(): void {
    this.syncModels();
    this.confirmChange.emit(this.translation);
    this.close();
  }

  public confirmClose(): void {
    if (!this.isModelChanged) {
      this.close();
    } else {
      const pendingChanges = new PendingChangesDialogComponent(this.dialog, this.translateService, {
        pendingChangesDescription: this.translateService.instant('general.api_translations_description_unsaved_changes'),
        canNotSave: !this.isValid,
        buttonOk: this.isCreate ? this.translateService.instant('general.api_translations_form_create') : undefined
      });
      pendingChanges.dialogRef.afterClosed().subscribe((res) => {
        if (res === true) {
          if (this.isCreate) {
            this.create();
          } else {
            this.update();
          }
        } else if (res === CONTINUE_WITHOUT_SAVING) {
          this.close();
        }
      });
    }
  }

  private close(): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close();
  }
}
